import React, { useEffect, useState } from 'react';
import { useDataProvider, useInput } from 'react-admin';
import { ReferenceArrayInput } from 'react-admin';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Typography,
    Chip,
    Tooltip,
} from '@material-ui/core';

const SelectStorageInput = ({ formData, source, ...props }) => {
    console.log('SelectStorageInput props:', props);
    const { input, meta } = useInput({ source, ...props });
    const { record } = props;
    const [choices, setChoices] = useState<any[]>([]);
    const [lastStorages, setLastStorages] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>(
        input && Array.isArray(input.value)
            ? input.value
                  .map(item => item?.storageId?.toString() || item)
                  .filter(Boolean)
            : []
    );
    const dataProvider = useDataProvider();
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
    const [numOfPages, setNumOfPages] = useState<number>(0);

    // Helper function to get all part IDs for a parent storage
    const getPartIds = (parentId: string | number): string[] => {
        return choices
            .filter(
                s => s.isPart && s.parentId?.toString() === parentId.toString()
            )
            .map(s => s.id.toString());
    };

    // Helper function to handle parent-child checkbox logic
    const handleCheckboxChange = (
        storageId: string,
        isChecked: boolean,
        storage: any
    ) => {
        let newStorageIds = [...selectedIds];

        if (isChecked) {
            // Add the clicked item
            if (!newStorageIds.includes(storageId)) {
                newStorageIds.push(storageId);
            }

            // If this is a part, also check its parent AND all sibling parts
            if (storage.isPart && storage.parentId) {
                const parentId = storage.parentId.toString();

                // Add the parent
                if (!newStorageIds.includes(parentId)) {
                    newStorageIds.push(parentId);
                }

                // Add ALL parts of this parent (including siblings)
                const allPartIds = getPartIds(parentId);
                allPartIds.forEach(partId => {
                    if (!newStorageIds.includes(partId)) {
                        newStorageIds.push(partId);
                    }
                });
            }

            // If this is a parent, also check all its parts
            if (!storage.isPart) {
                const partIds = getPartIds(storageId);
                partIds.forEach(partId => {
                    if (!newStorageIds.includes(partId)) {
                        newStorageIds.push(partId);
                    }
                });
            }
        } else {
            // Remove the clicked item
            newStorageIds = newStorageIds.filter(id => id !== storageId);

            // If this is a parent, also uncheck all its parts
            if (!storage.isPart) {
                const partIds = getPartIds(storageId);
                newStorageIds = newStorageIds.filter(
                    id => !partIds.includes(id)
                );
            }

            // If this is a part, uncheck parent and ALL sibling parts
            if (storage.isPart && storage.parentId) {
                const parentId = storage.parentId.toString();
                const allPartIds = getPartIds(parentId);

                // Uncheck the parent
                newStorageIds = newStorageIds.filter(id => id !== parentId);

                // Uncheck all sibling parts
                newStorageIds = newStorageIds.filter(
                    id => !allPartIds.includes(id)
                );
            }
        }

        return newStorageIds;
    };

    useEffect(() => {
        setIsLoading(true);
        const filterParams = {
            userId: formData.userId,
            ...(formData.orderId && {
                orderId: formData.orderId,
            }),
            statuses: ['stored', 'in'],
        };

        console.log('SelectStorageInput filter params:', filterParams);

        dataProvider
            .getList('storages', {
                filter: filterParams,
                pagination: { page: currentPage, perPage: 50 },
            })
            .then(async response => {
                console.log('Storage API response:', response);

                // Collect unique parent IDs from parts
                const parentIds = [
                    ...new Set(
                        response.data
                            .filter(item => item.isPart && item.parentId)
                            .map(item => item.parentId)
                    ),
                ];

                console.log('Parent IDs found in parts:', parentIds);

                // Check which parents are already in the response
                const existingParentIds = response.data
                    .filter(item => !item.isPart)
                    .map(item => item.id);

                // Find missing parent IDs
                const missingParentIds = parentIds.filter(
                    id => !existingParentIds.includes(id)
                );

                console.log('Missing parent IDs:', missingParentIds);

                let allStorages = [...response.data];

                // Fetch missing parents if any
                if (missingParentIds.length > 0) {
                    try {
                        const parentResponse = await dataProvider.getList(
                            'storages',
                            {
                                filter: {
                                    ids: missingParentIds,
                                },
                                pagination: { page: 1, perPage: 100 },
                            }
                        );

                        console.log(
                            'Fetched missing parents:',
                            parentResponse.data
                        );

                        // Add missing parents to the list
                        allStorages = [...allStorages, ...parentResponse.data];
                    } catch (error) {
                        console.error('Error fetching parent items:', error);
                    }
                }

                // Use perPage instead of response.limit which might be undefined
                const perPage = 50;
                const totalPages = Math.ceil(response.total / perPage) || 1;

                setHasNextPage(totalPages > currentPage);
                setHasPrevPage(currentPage > 1);

                if (allStorages && allStorages.length) {
                    allStorages.unshift({
                        id: 'select',
                        name: 'Select All in this page',
                    });
                }

                setNumOfPages(totalPages);
                setChoices(allStorages);

                if (allStorages.length === 0) {
                    setHasNextPage(false);
                    setHasPrevPage(false);
                }

                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error loading storages:', error);
                setIsLoading(false);
                setChoices([]);
            });
    }, [dataProvider, formData.userId, formData.orderId, currentPage]);

    return (
        <div>
            {!isLoading ? (
                <ReferenceArrayInput
                    label="Storages"
                    source="request_lines"
                    reference="storages"
                    resource="storages"
                    record={record}
                    format={request_lines =>
                        (request_lines || []).map(i => i && i.storageId)
                    }
                    parse={storageIds => {
                        // Ensure storageIds is an array
                        const safeStorageIds = Array.isArray(storageIds)
                            ? storageIds
                            : [];
                        let dataStorage: any[] = [];

                        // Check if 'select' is included in the array
                        const found = safeStorageIds.includes('select');

                        if (found) {
                            // Map each ID to an object with storageId property
                            let oldData = safeStorageIds.map(item => ({
                                storageId: item,
                            }));

                            // Map each choice to an object with storageId and other properties
                            let newDataStorage = choices
                                .filter(ele => ele && ele.id) // Filter out invalid choices
                                .map(ele => ({
                                    storageId: ele.id,
                                    ...(record &&
                                        record.request_lines &&
                                        record.request_lines.find(
                                            l => l && l.storageId === ele.id
                                        )),
                                }));

                            // Check if all newDataStorage items are in oldData
                            if (
                                newDataStorage.length > 0 &&
                                oldData.length > 0 &&
                                newDataStorage.every(
                                    v =>
                                        v &&
                                        v.storageId &&
                                        oldData.findIndex(
                                            i =>
                                                i &&
                                                i.storageId &&
                                                v.storageId === i.storageId
                                        ) !== -1
                                )
                            ) {
                                // Filter oldData to only include items that are in newDataStorage
                                dataStorage = oldData.filter(function (el) {
                                    return (
                                        el &&
                                        el.storageId &&
                                        newDataStorage.findIndex(
                                            item =>
                                                item &&
                                                item.storageId &&
                                                item.storageId === el.storageId
                                        ) !== -1
                                    );
                                });
                            } else {
                                dataStorage = newDataStorage;
                            }
                        } else {
                            // Check for differences between lastStorages and storageIds
                            let difference = lastStorages.filter(
                                x =>
                                    x &&
                                    x.storageId &&
                                    !safeStorageIds.includes(x.storageId)
                            );

                            if (
                                !difference.length ||
                                (difference[0] &&
                                    difference[0].storageId !== 'select')
                            ) {
                                // Map each ID to an object with storageId and other properties
                                dataStorage = safeStorageIds.map(id => ({
                                    storageId: id,
                                    ...(record &&
                                        record.request_lines &&
                                        record.request_lines.find(
                                            l => l && l.storageId === id
                                        )),
                                }));
                            }
                        }
                        // Make sure dataStorage is valid before setting it
                        const validDataStorage = Array.isArray(dataStorage)
                            ? dataStorage.filter(item => item && item.storageId)
                            : [];
                        setLastStorages(validDataStorage);
                        console.log('Parsed data storage:', validDataStorage);
                        return validDataStorage;
                    }}
                    filter={{
                        userId: formData.userId,
                        ...(formData.orderId && {
                            orderId: formData.orderId,
                        }),
                        statuses: ['stored', 'in'],
                    }}
                    perPage={200}
                    page={currentPage}
                    allowEmpty
                >
                    <div>
                        {console.log(
                            'Before rendering table - props.input:',
                            props.input
                        )}
                        <Typography
                            variant="subtitle1"
                            style={{ marginBottom: '10px' }}
                        >
                            Select storage items for this request:
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={
                                                    choices.length > 0 &&
                                                    choices.some(
                                                        s =>
                                                            s.id !== 'select' &&
                                                            selectedIds.includes(
                                                                s.id
                                                            )
                                                    ) &&
                                                    !choices.every(
                                                        s =>
                                                            s.id === 'select' ||
                                                            selectedIds.includes(
                                                                s.id
                                                            )
                                                    )
                                                }
                                                checked={
                                                    choices.length > 0 &&
                                                    choices.filter(
                                                        s => s.id !== 'select'
                                                    ).length > 0 &&
                                                    choices.every(
                                                        s =>
                                                            s.id === 'select' ||
                                                            selectedIds.includes(
                                                                s.id
                                                            )
                                                    )
                                                }
                                                onChange={e => {
                                                    const isChecked =
                                                        e.target.checked;
                                                    const newStorageIds = isChecked
                                                        ? choices
                                                              .filter(
                                                                  s =>
                                                                      s.id !==
                                                                      'select'
                                                              )
                                                              .map(s => s.id)
                                                        : [];
                                                    setSelectedIds(
                                                        newStorageIds
                                                    );
                                                    if (input?.onChange) {
                                                        // Convert to request_lines format
                                                        const requestLines = newStorageIds
                                                            .filter(
                                                                id =>
                                                                    id !==
                                                                    'select'
                                                            )
                                                            .map(id => ({
                                                                storageId:
                                                                    parseInt(
                                                                        id
                                                                    ) || id,
                                                            }));
                                                        input.onChange(
                                                            requestLines
                                                        );
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <strong>Storage ID</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Item Code</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Storage Size</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Storage Name</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Inventory</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Quantity</strong>
                                        </TableCell>
                                        {/* <TableCell>
                                            <strong>Type Size</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Type Name</strong>
                                        </TableCell> */}
                                        <TableCell>
                                            <strong>Status</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {choices
                                        .filter(s => s.id !== 'select')
                                        .map(storage => {
                                            const partIds = !storage.isPart
                                                ? getPartIds(storage.id)
                                                : [];
                                            const partCount = partIds.length;
                                            const isParent =
                                                !storage.isPart &&
                                                partCount > 0;

                                            // Debug logging
                                            if (
                                                storage.id === 255733 ||
                                                storage.isPart
                                            ) {
                                                console.log(
                                                    'Storage Item Debug:',
                                                    {
                                                        id: storage.id,
                                                        isPart: storage.isPart,
                                                        parentId:
                                                            storage.parentId,
                                                        partIds: partIds,
                                                        partCount: partCount,
                                                        isParent: isParent,
                                                    }
                                                );
                                            }

                                            return (
                                                <TableRow
                                                    key={storage.id}
                                                    style={{
                                                        backgroundColor: storage.isPart
                                                            ? '#f5f5f5'
                                                            : 'transparent',
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedIds.includes(
                                                                storage.id.toString()
                                                            )}
                                                            onChange={e => {
                                                                const isChecked =
                                                                    e.target
                                                                        .checked;
                                                                const newStorageIds = handleCheckboxChange(
                                                                    storage.id.toString(),
                                                                    isChecked,
                                                                    storage
                                                                );
                                                                setSelectedIds(
                                                                    newStorageIds
                                                                );
                                                                if (
                                                                    input?.onChange
                                                                ) {
                                                                    // Convert to request_lines format
                                                                    const requestLines = newStorageIds
                                                                        .filter(
                                                                            id =>
                                                                                id !==
                                                                                'select'
                                                                        )
                                                                        .map(
                                                                            id => ({
                                                                                storageId:
                                                                                    parseInt(
                                                                                        id
                                                                                    ) ||
                                                                                    id,
                                                                            })
                                                                        );
                                                                    input.onChange(
                                                                        requestLines
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {storage.id || 'N/A'}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {storage.code ||
                                                            '<unassigned>'}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {storage.size || 'N/A'}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {isParent && (
                                                            <Tooltip
                                                                title={`This item has ${partCount} part(s). IDs: ${partIds.join(
                                                                    ', '
                                                                )}`}
                                                            >
                                                                <span
                                                                    style={{
                                                                        marginRight:
                                                                            '8px',
                                                                    }}
                                                                >
                                                                    📦
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        {storage.isPart && '↳ '}
                                                        {storage.name
                                                            ? storage.name
                                                            : storage
                                                                  .storagetype
                                                                  ?.name ||
                                                              'N/A'}
                                                        {storage.isPart &&
                                                            ' (Part)'}
                                                        {isParent && (
                                                            <Chip
                                                                label={`${partCount} part${
                                                                    partCount >
                                                                    1
                                                                        ? 's'
                                                                        : ''
                                                                }`}
                                                                size="small"
                                                                color="primary"
                                                                style={{
                                                                    marginLeft:
                                                                        '8px',
                                                                    height:
                                                                        '20px',
                                                                }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {storage.storagetype
                                                            ?.inventory
                                                            ? 'Yes'
                                                            : 'No'}
                                                    </TableCell>
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {storage.qty ??
                                                            storage.storedQty ??
                                                            0}
                                                    </TableCell>

                                                    {/* <TableCell>
                                                    {storage.storagetype
                                                        ?.name || 'N/A'}
                                                </TableCell> */}
                                                    <TableCell
                                                        style={{
                                                            fontWeight: isParent
                                                                ? 'bold'
                                                                : 'normal',
                                                        }}
                                                    >
                                                        {storage.status ||
                                                            'N/A'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </ReferenceArrayInput>
            ) : (
                <h3>Loading...</h3>
            )}
            {choices.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '1rem',
                    }}
                >
                    {hasPrevPage && (
                        <button
                            style={{
                                backgroundColor: '#ECECEC',
                                padding: '.5rem 1rem',
                                marginRight: '1rem',
                                borderRadius: '4px',
                            }}
                            onClick={e => {
                                e.preventDefault();
                                setCurrentPage(prev => prev - 1);
                            }}
                        >
                            back
                        </button>
                    )}
                    <h3>{`Page ${currentPage} of ${numOfPages} pages`}</h3>
                    {hasNextPage && (
                        <button
                            style={{
                                backgroundColor: '#ECECEC',
                                padding: '.5rem 1rem',
                                borderRadius: '4px',
                            }}
                            onClick={e => {
                                e.preventDefault();
                                setCurrentPage(prev => prev + 1);
                            }}
                        >
                            next
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectStorageInput;
